import * as React from "react";
import * as RadixSelect from "@radix-ui/react-select";
import { ChevronDown } from "lucide-react";
import clsx from "clsx";
import { CheckIcon } from "./check-icon";

export function Select({
  value,
  onValueChange,
  children,
  placeholder,
  className,
  ...props
}: {
  value: string;
  onValueChange: (value: string) => void;
  children: React.ReactNode;
  placeholder?: string;
  className?: string;
} & React.ComponentPropsWithoutRef<typeof RadixSelect.Root>) {
  return (
    <RadixSelect.Root value={value} onValueChange={onValueChange} {...props}>
      <RadixSelect.Trigger
        className={clsx(
          "inline-flex items-center justify-between w-full rounded border border-gray-300 dark:border-gray-700 bg-card px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary",
          className
        )}
      >
        <RadixSelect.Value placeholder={placeholder} />
        <RadixSelect.Icon className="ml-2">
          <ChevronDown size={16} />
        </RadixSelect.Icon>
      </RadixSelect.Trigger>
      <RadixSelect.Portal>
        <RadixSelect.Content
          className="z-popover min-w-[var(--radix-select-trigger-width)] max-w-[95vw] rounded border border-gray-200 dark:border-gray-700 bg-card shadow-lg animate-in fade-in"
          position="popper"
        >
          <RadixSelect.Viewport className="p-1 max-h-[min(60vh,300px)] overflow-y-auto">
            {children}
          </RadixSelect.Viewport>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
}

export function SelectItem({
  children,
  className,
  ...props
}: {
  children: React.ReactNode;
  className?: string;
} & React.ComponentPropsWithoutRef<typeof RadixSelect.Item>) {
  return (
    <RadixSelect.Item
      className={clsx(
        "relative flex cursor-pointer select-none items-center rounded px-3 py-2 text-sm outline-none transition-colors data-[highlighted]:bg-primary/10 data-[state=checked]:font-semibold",
        className
      )}
      {...props}
    >
      <RadixSelect.ItemText>{children}</RadixSelect.ItemText>
      <RadixSelect.ItemIndicator className="absolute right-2">
        <CheckIcon size={16} variant="default" />
      </RadixSelect.ItemIndicator>
    </RadixSelect.Item>
  );
}

// Example usage:
// <Select value={value} onValueChange={setValue} placeholder="Select an option">
//   <SelectItem value="one">One</SelectItem>
//   <SelectItem value="two">Two</SelectItem>
// </Select>
